import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Permission, Role } from '../types';
import PermissionMatrixMobile from './PermissionMatrixMobile';

interface PermissionMatrixProps {
  permissions: Permission[];
  roles: Role[];
  rolePermissionDrafts: Record<number, number[]>;
  dirtyRoleIds: number[];
  isRefreshing: boolean;
  pendingAction: string | null;
  onEditPermission: (permission: Permission) => void;
  onDeletePermission: (permission: Permission) => Promise<boolean>;
  onTogglePermission: (roleId: number, permissionId: number, checked: boolean) => void;
  onSaveRole: (role: Role) => void;
}

const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  permissions,
  roles,
  rolePermissionDrafts,
  dirtyRoleIds,
  isRefreshing,
  pendingAction,
  onEditPermission,
  onDeletePermission,
  onTogglePermission,
  onSaveRole,
}) => {
  const { t } = useTranslation();
  const [permissionToDelete, setPermissionToDelete] = useState<Permission | null>(null);
  const isPermissionMutation = pendingAction === 'permission'
    || pendingAction?.startsWith('permission-delete-') === true;

  useEffect(() => {
    if (!permissionToDelete) return undefined;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && pendingAction === null) setPermissionToDelete(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [pendingAction, permissionToDelete]);

  const confirmDeletePermission = async () => {
    if (!permissionToDelete) return;
    const didDelete = await onDeletePermission(permissionToDelete);
    if (didDelete) setPermissionToDelete(null);
  };

  if (permissions.length === 0) {
    return (
      <div className="px-4 py-14 text-center text-secondary">
        <span className="material-symbols-outlined mb-2 text-4xl text-outline">key_off</span>
        <p className="font-medium text-on-surface">{t('admin.settings.matrix.empty_title')}</p>
        <p className="mt-1 text-body-sm">{t('admin.settings.matrix.empty_description')}</p>
      </div>
    );
  }

  return (
    <>
      <PermissionMatrixMobile
        permissions={permissions}
        roles={roles}
        rolePermissionDrafts={rolePermissionDrafts}
        dirtyRoleIds={dirtyRoleIds}
        isRefreshing={isRefreshing}
        pendingAction={pendingAction}
        onEditPermission={onEditPermission}
        onRequestDelete={setPermissionToDelete}
        onTogglePermission={onTogglePermission}
        onSaveRole={onSaveRole}
      />

      <div className="hidden max-h-[680px] overflow-auto lg:block">
        <table className="w-full min-w-[1080px] border-collapse text-left text-body-sm">
          <thead className="sticky top-0 z-20 bg-surface-container-low">
            <tr className="border-b border-outline-variant">
              <th className="sticky left-0 z-30 min-w-[280px] border-r border-outline-variant/60 bg-surface-container-low px-5 py-4 font-label-sm text-label-sm text-secondary">
                {t('admin.settings.matrix.permission')}
              </th>
              {roles.map((role) => {
                const isDirty = dirtyRoleIds.includes(role.id);
                const isSaving = pendingAction === `role-${role.id}`;
                return (
                  <th
                    key={role.id}
                    className="min-w-[180px] px-4 py-3 text-center align-top"
                    scope="col"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div>
                        <span className="block font-label-md font-semibold text-on-surface">{role.name}</span>
                        <span className="mt-0.5 block text-[11px] font-normal text-secondary">
                          {t('admin.settings.matrix.permission_count', { count: (rolePermissionDrafts[role.id] ?? []).length })}
                        </span>
                        {isDirty && (
                          <span className="mt-0.5 block text-[11px] font-normal text-primary">
                            {t('admin.settings.matrix.unsaved_changes')}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="inline-flex min-h-9 items-center justify-center gap-1.5 rounded-lg border border-outline-variant bg-white px-3 font-label-sm text-primary transition-colors hover:bg-primary-fixed disabled:cursor-not-allowed disabled:text-outline"
                        disabled={!isDirty || isRefreshing || pendingAction !== null}
                        onClick={() => onSaveRole(role)}
                      >
                        {isSaving && <span className="material-symbols-outlined animate-spin text-[16px]">progress_activity</span>}
                        {isSaving
                          ? t('admin.settings.actions.saving')
                          : t('admin.settings.actions.save_changes')}
                      </button>
                    </div>
                  </th>
                );
              })}
              <th className="sticky right-0 z-30 min-w-[112px] border-l border-outline-variant/60 bg-surface-container-low px-3 py-4 text-center font-label-sm text-label-sm text-secondary">
                {t('admin.settings.matrix.action')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/60 bg-white">
            {permissions.map((permission) => (
              <tr key={permission.id} className="group hover:bg-surface-container-lowest">
                <th
                  className="sticky left-0 z-10 border-r border-outline-variant/60 bg-white px-5 py-4 font-normal group-hover:bg-surface-container-lowest"
                  scope="row"
                >
                  <span className="block font-mono text-[13px] font-semibold text-on-surface">
                    {permission.name}
                  </span>
                  <span className="mt-0.5 block max-w-[320px] whitespace-normal text-body-sm text-secondary">
                    {permission.description}
                  </span>
                </th>
                {roles.map((role) => {
                  const checked = (rolePermissionDrafts[role.id] ?? []).includes(permission.id);
                  return (
                    <td key={role.id} className="px-4 py-3 text-center">
                      <label className="inline-flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg hover:bg-primary-fixed/40 focus-within:ring-2 focus-within:ring-primary/30">
                        <input
                          type="checkbox"
                          className="h-[18px] w-[18px] cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-50"
                          checked={checked}
                          disabled={isRefreshing || pendingAction === `role-${role.id}` || isPermissionMutation}
                          aria-label={t('admin.settings.matrix.toggle_label', {
                            permission: permission.name,
                            role: role.name,
                          })}
                          onChange={(event) =>
                            onTogglePermission(role.id, permission.id, event.target.checked)}
                        />
                      </label>
                    </td>
                  );
                })}
                <td className="sticky right-0 z-10 border-l border-outline-variant/60 bg-white px-3 py-3 text-center group-hover:bg-surface-container-lowest">
                  <div className="flex items-center justify-center gap-1">
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-secondary transition-colors hover:bg-primary-fixed hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-50"
                      disabled={isRefreshing || pendingAction !== null}
                      aria-label={t('admin.settings.actions.edit_permission_label', {
                        permission: permission.name,
                      })}
                      title={t('admin.settings.actions.edit_permission')}
                      onClick={() => onEditPermission(permission)}
                    >
                      <span className="material-symbols-outlined text-[19px]">edit</span>
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-error transition-colors hover:bg-error-container/60 focus:outline-none focus:ring-2 focus:ring-error/30 disabled:opacity-50"
                      disabled={isRefreshing || pendingAction !== null}
                      aria-label={t('admin.settings.actions.delete_permission_label', {
                        permission: permission.name,
                      })}
                      title={t('admin.settings.actions.delete_permission')}
                      onClick={() => setPermissionToDelete(permission)}
                    >
                      <span className="material-symbols-outlined text-[19px]">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {permissionToDelete && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && pendingAction === null) {
              setPermissionToDelete(null);
            }
          }}
        >
          <section
            className="w-full max-w-md rounded-[20px] border border-outline-variant bg-white p-5 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-permission-title"
            aria-describedby="delete-permission-description"
          >
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined rounded-full bg-error-container p-2 text-error">
                delete
              </span>
              <div>
                <h3 id="delete-permission-title" className="font-h3 text-h3 text-on-surface">
                  {t('admin.settings.delete_dialog.title')}
                </h3>
                <p id="delete-permission-description" className="mt-2 text-body-md text-secondary">
                  {t('admin.settings.delete_dialog.description', {
                    permission: permissionToDelete.name,
                  })}
                </p>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                autoFocus
                type="button"
                disabled={pendingAction !== null}
                className="min-h-10 rounded-xl border border-outline-variant bg-white px-4 font-label-md text-secondary hover:bg-surface-container-low disabled:opacity-60"
                onClick={() => setPermissionToDelete(null)}
              >
                {t('admin.settings.actions.cancel')}
              </button>
              <button
                type="button"
                disabled={pendingAction !== null}
                className="min-h-10 rounded-xl bg-error px-4 font-label-md text-on-error hover:bg-on-error-container disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => void confirmDeletePermission()}
              >
                {pendingAction === `permission-delete-${permissionToDelete.id}`
                  ? t('admin.settings.actions.deleting')
                  : t('admin.settings.actions.confirm_delete')}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default PermissionMatrix;
